import { format } from 'date-fns';
import { FC } from 'react';
import { FiCalendar, FiCheck } from 'react-icons/fi';
import { NavPage } from '../../components/page/Breadcrumbs';
import { PageChangeDirection, PageHeader } from '../../components/page/PageHeader';
import { RosterProvider } from './providers/RosterService';
import { Roster } from './Roster';
import { useSelectedDate } from '@/services/useSelectedDate';
import { PageContainer } from '@/components/page/PageContainer';
import { useSaveRoster } from './hooks/useSaveRoster';
import { Button } from '@/components/shared/button/Button';
import { strings } from '@/services/translation/strings';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import { usePublishWorkingRoster } from './hooks/usePublishWorkingRoster';
import { useWorkingRoster } from './providers/WorkingRosterService';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { useNavigate } from 'react-router-dom';

const RosterPageInner: FC<{ date: Date }> = ({ date }) => {
  const startOfMonth = new Date(date);
  startOfMonth.setUTCDate(1);

  const breadcrumbs: NavPage[] = [
    {
      name: 'Rosters',
      to: `/rosters/calendar/${dateToApiFormat(startOfMonth)}`
    },
    {
      name: format(date, 'yyyy/MM/dd'),
      to: '/rosters/' + dateToApiFormat(date)
    }
  ];

  const { saveRoster, fetching: fetchingSave } = useSaveRoster();
  const { publishRoster, fetching: fetchingPublish } = usePublishWorkingRoster();

  const { workingRoster } = useWorkingRoster();
  const published = workingRoster?.published;

  const saveNeeded = useConfirm(s => s.confirmationNeeded);

  const navigate = useNavigate();
  const onChangePage = (direction: PageChangeDirection) => {
    const newDate = new Date(date);

    if (direction === PageChangeDirection.PREV) {
      newDate.setUTCDate(newDate.getUTCDate() - 1);
    } else if (direction === PageChangeDirection.NEXT) {
      newDate.setUTCDate(newDate.getUTCDate() + 1);
    }

    navigate(`/rosters/${dateToApiFormat(newDate)}`);
  };

  return (
    <PageContainer
      lockHeader
      fullWidth
      customHeader={
        <PageHeader
          fullWidth
          centeredTitle
          pageTitle={format(date, 'EEEE do MMMM yyyy')}
          icon={<FiCalendar />}
          breadcrumbs={breadcrumbs}
          onChangePage={onChangePage}
        >
          {workingRoster && (
            <div className="flex gap-3 items-center">
              <Button primary disabled={!saveNeeded} loading={fetchingSave} onClick={saveRoster}>
                {strings.common.save}
              </Button>
              <Button
                primary={published}
                secondary={!published}
                disabled={saveNeeded || fetchingSave}
                loading={fetchingPublish}
                onClick={!published ? publishRoster : undefined}
                className="flex gap-2 items-center"
              >
                {published && <FiCheck className="h-6 w-6" />}
                {published ? strings.daily.roster.published : strings.daily.roster.publish}
              </Button>
            </div>
          )}
        </PageHeader>
      }
    >
      <div className="h-full flex flex-col flex-1">
        <Roster />
      </div>
    </PageContainer>
  );
};

export const RosterPage: FC = () => {
  const date = useSelectedDate();

  return (
    <RosterProvider date={date}>
      <RosterPageInner date={date} />
    </RosterProvider>
  );
};
